import React, { useState, useEffect, useRef, useCallback } from "react";
import { Checkbox, Button, DatePicker, Radio, Select, Space } from "antd";
import { DownloadOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import Service from "../../../services";
import styles from "./index.module.scss";
import { CSVLink } from "react-csv";
import { TRADING_PAIRS, TRADING_PAIR_COLORS } from "../../../utils";
import { applyTradingHoursFilterToSeriesList, HOURS_FILTER } from "../../../utils/tradingHoursUtils";
import FavouriteSymbols from "../../../components/ModelPerformance/FavouriteSymbols";
import BestSymbols from "../../../components/ModelPerformance/BestSymbols";
import dayjs from "dayjs";

const CheckboxGroup = Checkbox.Group;
const checkBoxOptions = ["Enigma", "EON"];

const SYMBOLS = [
  ...new Set(
    TRADING_PAIRS.map((pair) => {
      let symbol = pair.split("/")[0];
      if (symbol.startsWith("1000")) {
        // Remove the "1000" prefix for the binance_usds_future symbols
        symbol = symbol.slice(4);
      }
      return symbol;
    })
  ),
];

const CHART_TYPE = {
  accumulative: "accumulative",
  bestsymbols: "bestsymbols",
};

const { Option } = Select;
const { RangePicker } = DatePicker;

export const availableTimeIntervals = ["5m", "15m", "20m", "25m", "30m", "35m", "40m", "45m", "1h", "70m", "80m", "90m", "2h", "3h", "4h"];

function ModelPerformance() {
  const chartRef = useRef(null);
  const visibilityStateRef = useRef(new Map()); // Track visibility separately

  const [rawCumulatedPNLList, setRawCumulatedPNLList] = useState([]); // Raw data from server (unfiltered)
  const [cumulatedPNLList, setCumulatedPNLList] = useState([]);
  const [visibilityVersion, setVisibilityVersion] = useState(0); // Counter to force re-render when visibility changes
  const [symbol, setSymbol] = useState("ALL");
  const [pnlVisibility, setPnlVisibility] = useState(true);
  const [timestamp_15mPointNumStart, setTimestamp_15mPointNumStart] = useState(Math.trunc((Date.now() - 7 * 24 * 3600 * 1000) / 1000));
  const [timestamp_15mPointNumEnd, setTimestamp_15mPointNumEnd] = useState(Math.trunc(Date.now() / 1000));
  const [visibleIntervals, setVisibleIntervals] = useState([...availableTimeIntervals]);
  const [availableFeatureSets, setAvailableFeatureSets] = useState([]);
  const [visibleFeatureSets, setVisibleFeatureSets] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [chartType, setChartType] = useState(CHART_TYPE.accumulative);
  const [checkedList, setCheckedList] = useState([...checkBoxOptions]);
  const [bestSymbolsPNLList, setBestSymbolsPNLList] = useState([]);
  const [hoursFilter, setHoursFilter] = useState(HOURS_FILTER.all);

  const extractFeatureSets = useCallback((modelNames) => {
    const featureSetRegex = /fs_[^_]+(?:_[^_]+)*_v\d+/;
    const featureSets = new Set();
    modelNames.forEach((modelName) => {
      const match = modelName.match(featureSetRegex);
      if (match) {
        featureSets.add(match[0]);
      }
    });
    return Array.from(featureSets);
  }, []);

  const updateChartExtremes = useCallback(() => {
    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      chart.xAxis[0].setExtremes(null, null);
    }
  }, []);

  const fetchHistoricalDataForAllSymbols = useCallback(async () => {
    const allSymbolsData = await Promise.all(
      SYMBOLS.map((symbol) =>
        (async () => {
          const isWithin32Days = Date.now() - timestamp_15mPointNumStart * 1000 < 32 * 24 * 3600 * 1000;

          const { cumulatedPNLList } = await Service.listBacktestKPIs(
            symbol,
            pnlVisibility,
            timestamp_15mPointNumStart,
            timestamp_15mPointNumEnd,
            false,
            isWithin32Days ? 4 : 48,
            false
          );

          const cumulatedPNL = cumulatedPNLList.filter((cumulatedPNL) => cumulatedPNL.name === "averagePNL_Enigma")[0];
          cumulatedPNL.name = symbol;
          cumulatedPNL.zones.forEach((zone) => {
            delete zone.color; // delete customized color to ensure label and spline color match
          });

          cumulatedPNL.color = TRADING_PAIR_COLORS[symbol];
          const cumulatedPNLEonRaw = cumulatedPNLList.filter((cumulatedPNL) => cumulatedPNL.name === "averagePNL_EON");
          let cumulatedPNLEon = null;

          if (cumulatedPNLEonRaw.length > 0) {
            cumulatedPNLEon = cumulatedPNLEonRaw[0];
            cumulatedPNLEon.name = `${symbol}_EON`;
            cumulatedPNLEon.zones.forEach((zone) => {
              delete zone.color; // delete customized color to ensure label and spline color match
            });
            cumulatedPNLEon.color = TRADING_PAIR_COLORS[symbol];
          }

          return {
            cumulatedPNL,
            cumulatedPNLEon,
          };
        })()
      )
    );

    const cumulatedPNLList = [];
    allSymbolsData.forEach(({ cumulatedPNL, cumulatedPNLEon }) => {
      cumulatedPNLList.push(cumulatedPNL);
      if (cumulatedPNLEon) {
        cumulatedPNLList.push(cumulatedPNLEon);
      }
    });

    return { cumulatedPNLList };
  }, [timestamp_15mPointNumStart, timestamp_15mPointNumEnd, pnlVisibility]);

  const fetchHistoricalData = useCallback(async () => {
    setLoadingData(true);
    let cumulatedPNLListData;

    if (symbol === "ALL") {
      ({ cumulatedPNLList: cumulatedPNLListData } = await fetchHistoricalDataForAllSymbols());
    } else {
      const [start, end] = [timestamp_15mPointNumStart, timestamp_15mPointNumEnd];
      ({ cumulatedPNLList: cumulatedPNLListData } = await Service.listBacktestKPIs(symbol, pnlVisibility, start, end, true, 4, false));
    }

    const availableFeatureSetsData = extractFeatureSets(cumulatedPNLListData.map((pnl) => pnl.name));

    // Store raw data (will be cloned when filtering)
    setRawCumulatedPNLList(cumulatedPNLListData);
    setCumulatedPNLList(cumulatedPNLListData);
    setLoadingData(false);
    setAvailableFeatureSets(availableFeatureSetsData);
    setVisibleFeatureSets(availableFeatureSetsData);

    // Call updateChartExtremes after state is set
    setTimeout(updateChartExtremes, 0);
  }, [
    symbol,
    timestamp_15mPointNumStart,
    timestamp_15mPointNumEnd,
    pnlVisibility,
    fetchHistoricalDataForAllSymbols,
    extractFeatureSets,
    updateChartExtremes,
  ]);

  useEffect(() => {
    // Configure Highcharts to use user's local timezone instead of UTC
    Highcharts.setOptions({
      time: {
        useUTC: false,
      },
    });

    const initializeData = async () => {
      try {
        await fetchHistoricalData();
      } catch (error) {
        console.error("initialization failed", error);
      }
    };
    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply trading hours filtering on client side when filter settings change
  useEffect(() => {
    if (rawCumulatedPNLList.length === 0) return;

    const filtered = applyTradingHoursFilterToSeriesList(rawCumulatedPNLList, hoursFilter);

    // Apply saved visibility state from ref (create new objects to avoid mutating frozen data)
    const filteredWithVisibility = filtered.map((series) => {
      let visible;
      if (visibilityStateRef.current.has(series.name)) {
        visible = visibilityStateRef.current.get(series.name);
      } else {
        // Default visibility for new series
        visible = true;
        visibilityStateRef.current.set(series.name, true);
      }
      return { ...series, visible };
    });

    setCumulatedPNLList(filteredWithVisibility);
  }, [rawCumulatedPNLList, hoursFilter, visibilityVersion]);

  const updatePNLListVisibility = useCallback(() => {
    // Update visibility in ref only, don't modify raw data
    rawCumulatedPNLList.forEach((pnl) => {
      const nameContainsChecklistKeyword = checkedList.some((item) => pnl.name.includes(item));
      const intervals = [pnl.name.split(".")[1], pnl.name.split("_")[1]];
      const intervalIsVisible = intervals.some((interval) => visibleIntervals.includes(interval));
      const featureSetIsVisible = visibleFeatureSets.some((featureSet) => pnl.name.includes(featureSet));
      const isAveragePnl = pnl.name.includes("averagePNL");
      const visible = nameContainsChecklistKeyword && (intervalIsVisible || isAveragePnl) && (featureSetIsVisible || isAveragePnl);

      // Set visibility for series
      visibilityStateRef.current.set(pnl.name, visible);
    });

    // Force re-render by incrementing version
    setVisibilityVersion((v) => v + 1);
    setPnlVisibility(!pnlVisibility);
  }, [rawCumulatedPNLList, checkedList, visibleIntervals, visibleFeatureSets, pnlVisibility]);

  const handleSymbolVisibility = useCallback(
    ({ symbolsToShow = [], showAll = null } = {}) => {
      // Update visibility in ref only, don't modify data
      cumulatedPNLList.forEach((symbol) => {
        let isVisible = visibilityStateRef.current.get(symbol.name) ?? true;
        if (showAll !== null) {
          isVisible = showAll;
        } else if (symbolsToShow.length > 0) {
          isVisible = symbolsToShow.includes(symbol.name);
        }
        visibilityStateRef.current.set(symbol.name, isVisible);
      });

      // Force re-render
      setVisibilityVersion((v) => v + 1);
      setChartType(CHART_TYPE.accumulative);
    },
    [cumulatedPNLList]
  );

  const handleBestSymbolsChange = useCallback((bestSymbols) => {
    setBestSymbolsPNLList(bestSymbols);
    setChartType(CHART_TYPE.bestsymbols);
  }, []);

  const generateCSVDataForAllSymbols = useCallback((csvFileName, seriesData) => {
    const csvData = [[]];

    for (let i = 0; i < seriesData.length; i++) {
      const symbolData = seriesData[i];
      csvData[0].push("timestamp", "date", symbolData.name);

      const averagePNLList = symbolData.data;
      for (let j = 0; j < averagePNLList.length; j++) {
        const pnl = averagePNLList[j];
        const row = j + 1;
        const col = i * 3;
        if (csvData[row] === undefined) {
          csvData[row] = [];
        }
        csvData[row][col] = String(pnl.x);
        csvData[row][col + 1] = new Date(pnl.x).toISOString();
        csvData[row][col + 2] = pnl.y === undefined ? "0" : pnl.y.toFixed(4);
      }
    }

    return csvData;
  }, []);

  const generateCSVDataForSingleSymbol = useCallback((csvFileName, seriesData) => {
    const seriesDataCopy = [...seriesData];

    for (let i = 0; i < seriesDataCopy.length; i++) {
      const name = seriesDataCopy[i].name;
      if (name === "averagePNLAllModels_Enigma") {
        [seriesDataCopy[0], seriesDataCopy[i]] = [seriesDataCopy[i], seriesDataCopy[0]];
        break;
      }
    }

    const csvData = [[]];

    for (let i = 0; i < seriesDataCopy.length; i++) {
      const symbolData = seriesDataCopy[i];
      csvData[0].push("timestamp", "date", symbolData.name);

      const modelPNLList = symbolData.data;
      for (let j = 0; j < modelPNLList.length; j++) {
        const pnl = modelPNLList[j];
        const row = j + 1;
        const col = i * 3;
        if (csvData[row] === undefined) {
          csvData[row] = [];
        }
        csvData[row][col] = String(pnl.x);
        csvData[row][col + 1] = new Date(pnl.x).toISOString();
        csvData[row][col + 2] = pnl.y === undefined ? "0" : pnl.y.toFixed(4);
      }
    }

    return csvData;
  }, []);

  const generateCSVData = useCallback(
    (csvFileName, seriesData) => {
      if (seriesData.length === 0) return [];

      if (SYMBOLS.includes(seriesData[0].name)) {
        return generateCSVDataForAllSymbols(csvFileName, seriesData);
      } else {
        return generateCSVDataForSingleSymbol(csvFileName, seriesData);
      }
    },
    [generateCSVDataForAllSymbols, generateCSVDataForSingleSymbol]
  );

  const legendItemClickAccumulatedPNL = useCallback((event) => {
    const symbolName = event.target.name;
    const newVisibility = !event.target.visible;

    // Save to visibility map only
    visibilityStateRef.current.set(symbolName, newVisibility);

    // Force re-render by incrementing version
    setVisibilityVersion((v) => v + 1);

    // Return false to prevent default Highcharts behavior (we're handling it in React state)
    return false;
  }, []);

  const legendItemClick = useCallback(
    (event) => {
      // All chart types use the same legend click handler now
      return legendItemClickAccumulatedPNL(event);
    },
    [legendItemClickAccumulatedPNL]
  );

  const typeToYAxis = {
    [CHART_TYPE.accumulative]: {
      title: {
        text: "Accumulative PNL",
      },
      labels: {
        format: "{value}",
      },
    },
    [CHART_TYPE.bestsymbols]: {
      title: {
        text: "Best Symbols PNL",
      },
      labels: {
        format: "{value}",
      },
    },
  };

  let seriesData;
  let csvFileName;

  if (chartType === CHART_TYPE.bestsymbols) {
    seriesData = bestSymbolsPNLList;
    csvFileName = "bestSymbolsPNL";
  } else {
    seriesData = cumulatedPNLList;
    csvFileName = "cumulatedPNL";
  }

  const csvFile = generateCSVData(csvFileName, seriesData);

  const options = {
    rangeSelector: {
      selected: 5, // select All in chart
    },
    chart: {
      height: 1000,
    },
    credits: {
      enabled: false,
    },
    title: { text: "Profit and Loss History" },
    yAxis: typeToYAxis[chartType],
    exporting: { enabled: false },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        day: "%H:%M %d %b %Y",
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: legendItemClick,
        },
      },
    },
    series: JSON.parse(JSON.stringify(seriesData)),
  };

  return (
    <>
      <div className={styles.modelPerformanceHeader}>
        <div className={styles.title}>Model Performance</div>

        {/* Time range controls - centered */}
        <div className={styles.modelPerformance} style={{ justifyContent: "center" }}>
          <Select
            showSearch
            value={symbol}
            style={{ width: 120, marginRight: 10 }}
            onChange={(value) => {
              setSymbol(value);
              if (chartType === CHART_TYPE.bestsymbols) {
                setChartType(CHART_TYPE.accumulative);
              }
            }}
            optionFilterProp="children"
          >
            {[
              <Option value={"ALL"} key="ALL">
                {"ALL"}
              </Option>,
              ...SYMBOLS.map((sym) => (
                <Option value={sym} key={sym}>
                  {sym}
                </Option>
              )),
            ]}
          </Select>
          <RangePicker
            showTime={{
              hideDisabledOptions: true,
              defaultValue: [dayjs("00:00:00", "HH:mm:ss"), dayjs("23:59:59", "HH:mm:ss")],
            }}
            style={{ marginRight: 10 }}
            onChange={(dates, dateStrs) => {
              const [start, end] = dates.map((date) => date.unix());
              setTimestamp_15mPointNumStart(start);
              setTimestamp_15mPointNumEnd(end);
              setTimeout(updateChartExtremes, 0);
            }}
          />
          <Button type="primary" onClick={fetchHistoricalData} loading={loadingData}>
            Update
          </Button>
        </div>

        {/* Trading hours controls - centered */}
        <div className={styles.modelPerformance} style={{ justifyContent: "center" }}>
          <Radio.Group
            value={hoursFilter}
            onChange={(e) => {
              setHoursFilter(e.target.value);
            }}
            style={{ marginRight: 10 }}
          >
            <Radio.Button value={HOURS_FILTER.all}>All Hours</Radio.Button>
            <Radio.Button value={HOURS_FILTER.trading}>Trading Hours</Radio.Button>
          </Radio.Group>
        </div>
      </div>
      {csvFile.length > 0 && (
        <div>
          <CSVLink data={csvFile} filename={`${csvFileName}.csv`}>
            <DownloadOutlined style={{ marginRight: 5 }} />
            Download csv
          </CSVLink>
        </div>
      )}
      <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType={"stockChart"} options={options} />
      {chartType === CHART_TYPE.bestsymbols && symbol === "ALL" && (
        <div className={styles.filtrate}>
          <Space>
            <BestSymbols cumulatedPNLList={cumulatedPNLList} onPeriodSelect={handleBestSymbolsChange} />
            <span>|</span>
            <FavouriteSymbols
              favouritePNLList={bestSymbolsPNLList}
              updateSymbolVisibility={(favouriteSymbols) => handleSymbolVisibility({ symbolsToShow: favouriteSymbols })}
            />
            <span>|</span>
            <Button icon={<EyeOutlined style={{ marginRight: 5 }} />} onClick={() => handleSymbolVisibility({ showAll: true })}>
              Show All
            </Button>
            <Button icon={<EyeInvisibleOutlined style={{ marginRight: 5 }} />} onClick={() => handleSymbolVisibility({ showAll: false })}>
              Clear All
            </Button>
          </Space>
        </div>
      )}
      {chartType === CHART_TYPE.accumulative && symbol === "ALL" && (
        <div className={styles.filtrate}>
          <Space>
            <BestSymbols cumulatedPNLList={cumulatedPNLList} onPeriodSelect={handleBestSymbolsChange} />
            <span>|</span>
            <FavouriteSymbols
              favouritePNLList={cumulatedPNLList}
              updateSymbolVisibility={(favouriteSymbols) => handleSymbolVisibility({ symbolsToShow: favouriteSymbols })}
            />
            <span>|</span>
            <Button icon={<EyeOutlined style={{ marginRight: 5 }} />} onClick={() => handleSymbolVisibility({ showAll: true })}>
              Show All
            </Button>
            <Button icon={<EyeInvisibleOutlined style={{ marginRight: 5 }} />} onClick={() => handleSymbolVisibility({ showAll: false })}>
              Clear All
            </Button>
          </Space>
        </div>
      )}
      {chartType === CHART_TYPE.accumulative && symbol !== "ALL" && (
        <div className={styles.filtrate}>
          <Space>
            <CheckboxGroup
              options={checkBoxOptions}
              value={checkedList}
              onChange={(list) => {
                setCheckedList(list);
                setTimeout(updatePNLListVisibility, 0);
              }}
            />
            <Select
              mode="multiple"
              allowClear
              style={{ height: 60 }}
              placeholder="Please select feature sets"
              value={visibleFeatureSets}
              onChange={(value) => setVisibleFeatureSets(value)}
            >
              {availableFeatureSets.map((featureSet) => (
                <Option key={featureSet}>{featureSet}</Option>
              ))}
            </Select>
            <Select
              mode="multiple"
              allowClear
              style={{ width: 500 }}
              placeholder="Please select"
              value={visibleIntervals}
              onChange={(value) => setVisibleIntervals(value)}
            >
              {availableTimeIntervals.map((interval) => (
                <Option key={interval}>{interval}</Option>
              ))}
            </Select>
            <Button type="primary" onClick={updatePNLListVisibility}>
              Update Visibility
            </Button>
          </Space>
        </div>
      )}
    </>
  );
}

export default ModelPerformance;
